import { CartAddEvent, ThemeEvents } from '@theme/events';

const cartRoot = (value) => `${(value || '/').replace(/\/+$/, '')}/`;

class SovahCartShipping extends HTMLElement {
  connectedCallback() {
    this.abortController?.abort();
    this.abortController = new AbortController();
    this.querySelector('form')?.addEventListener('submit', this.calculate, { signal: this.abortController.signal });
    this.querySelector('form')?.addEventListener('input', this.invalidate, { signal: this.abortController.signal });
    this.previousSubtotal = this.dataset.subtotal;
    this.observer = new MutationObserver(() => {
      if (this.previousSubtotal !== this.dataset.subtotal) {
        this.previousSubtotal = this.dataset.subtotal;
        this.invalidate();
      }
    });
    this.observer.observe(this, { attributes: true, attributeFilter: ['data-subtotal'] });
    document.addEventListener(ThemeEvents.cartUpdate, this.invalidate, { signal: this.abortController.signal });
    document.addEventListener(ThemeEvents.discountUpdate, this.invalidate, { signal: this.abortController.signal });
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.requestController?.abort();
    this.observer?.disconnect();
  }

  invalidate = () => {
    this.requestController?.abort();
    const result = this.querySelector('.sovah-cart-shipping__result');
    if (result) result.replaceChildren();
    const button = this.querySelector('button[type="submit"]');
    if (button) button.disabled = false;
    this.removeAttribute('aria-busy');
  };

  calculate = async (event) => {
    event.preventDefault();
    this.requestController?.abort();
    const controller = new AbortController();
    this.requestController = controller;
    const timeout = setTimeout(() => controller.abort(), 45000);
    const form = event.currentTarget;
    const result = this.querySelector('.sovah-cart-shipping__result');
    const button = form.querySelector('button[type="submit"]');
    const country = form.elements.country.value;
    const zip = form.elements.zip.value.trim();
    const query = new URLSearchParams({ 'shipping_address[country]': country, 'shipping_address[zip]': zip });
    const root = cartRoot(this.dataset.root);
    let shippingRoot = root;
    button.disabled = true;
    this.setAttribute('aria-busy', 'true');
    result.textContent = this.dataset.loading;
    try {
      let response = await fetch(`${shippingRoot}cart/prepare_shipping_rates.json?${query}`, { method: 'POST', signal: controller.signal, headers: { Accept: 'application/json' } });
      if (response.status === 404 && shippingRoot !== '/') {
        shippingRoot = '/';
        response = await fetch(`${shippingRoot}cart/prepare_shipping_rates.json?${query}`, { method: 'POST', signal: controller.signal, headers: { Accept: 'application/json' } });
      }
      if (!response.ok) throw new Error(`Shipping preparation HTTP ${response.status}`);
      let rates;
      for (let attempt = 0; attempt < 40; attempt += 1) {
        const rateResponse = await fetch(`${shippingRoot}cart/async_shipping_rates.json?${query}`, { signal: controller.signal, headers: { Accept: 'application/json' } });
        if (!rateResponse.ok) throw new Error(`Shipping rates HTTP ${rateResponse.status}`);
        const data = await rateResponse.json();
        if (data && Array.isArray(data.shipping_rates)) {
          rates = data.shipping_rates;
          break;
        }
        await new Promise((resolve, reject) => {
          const timer = setTimeout(resolve, 700);
          controller.signal.addEventListener('abort', () => { clearTimeout(timer); reject(new DOMException('Aborted', 'AbortError')); }, { once: true });
        });
      }
      if (!rates) throw new Error('Shipping timeout');
      if (controller.signal.aborted || !this.isConnected) return;
      const amounts = rates.map(rate => Number(rate.price)).filter(amount => Number.isFinite(amount) && amount >= 0);
      result.replaceChildren();
      if (!amounts.length) {
        result.textContent = this.dataset.noRates;
        return;
      }
      const cheapest = Math.min(...amounts);
      const formatter = new Intl.NumberFormat(document.documentElement.lang, { style: 'currency', currency: this.dataset.currency || 'EUR' });
      const price = document.createElement('strong');
      price.textContent = cheapest === 0 ? this.dataset.free : `${this.dataset.from} ${formatter.format(cheapest)}`;
      const total = document.createElement('p');
      total.textContent = `${this.dataset.total}: ${formatter.format(Number(this.dataset.subtotal) / 100 + cheapest)}`;
      result.append(price, total);
    } catch (error) {
      if (error.name !== 'AbortError') console.warn('SOVAH shipping estimate:', error.message);
      if (this.requestController === controller && this.isConnected && result.textContent) result.textContent = this.dataset.error;
    } finally {
      clearTimeout(timeout);
      if (this.requestController === controller) {
        button.disabled = false;
        this.removeAttribute('aria-busy');
      }
    }
  };
}

class SovahCartRecommendation extends HTMLElement {
  connectedCallback() {
    this.listenerController?.abort();
    this.listenerController = new AbortController();
    this.querySelector('form')?.addEventListener('submit', this.add, { signal: this.listenerController.signal });
  }

  disconnectedCallback() {
    this.listenerController?.abort();
  }

  add = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    const form = event.currentTarget;
    const button = form.querySelector('button[type="submit"]');
    if (button.disabled) return;
    const error = this.querySelector('[role="alert"]');
    error.hidden = true;
    const label = button.textContent;
    button.disabled = true;
    button.textContent = this.dataset.adding;
    try {
      const sections = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map(element => element.dataset.sectionId))].slice(0, 5);
      const root = cartRoot(this.dataset.root);
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: form.elements.id.value, quantity: 1 }], sections, sections_url: window.location.pathname })
      });
      const data = await response.json();
      if (!response.ok) throw new Error('Unavailable');
      const previousCount = Number(document.querySelector('[ref="cartItemCount"]')?.textContent || 0);
      const cart = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } }).then(response => response.ok ? response.json() : null).catch(() => null);
      document.dispatchEvent(new CartAddEvent(cart || data, 'sovah-cart-recommendation', { source: 'sovah-cart-recommendation', itemCount: cart?.item_count || previousCount + 1, sections: data.sections }));
    } catch (failure) {
      if (this.isConnected) {
        error.textContent = this.dataset.error;
        error.hidden = false;
      }
    } finally {
      if (this.isConnected) {
        button.disabled = false;
        button.textContent = label;
      }
    }
  };
}

if (!customElements.get('sovah-cart-shipping')) customElements.define('sovah-cart-shipping', SovahCartShipping);
if (!customElements.get('sovah-cart-recommendation')) customElements.define('sovah-cart-recommendation', SovahCartRecommendation);
